import { SubscriptionTier } from "@repo/ui/pricing-hooks";
import { Button, Modal } from "@shapr3d/frontend-lib";

import { useTracker } from "../../../hooks/useTracker";
import { ElementClickAction, ElementClickLocation } from "../../../lib/TrackingAdapter";
import { getTierDisplayName } from "../subscriptionUtils";

import style from "./SubscriptionCancelModal.module.css";

const CancelMessage: React.FC<{
  editorSeatCount: number;
  isSubscriptionInGracePeriod: boolean;
  subscriptionExpirationDate: string;
  subscriptionTier: SubscriptionTier;
}> = ({
  editorSeatCount,
  subscriptionExpirationDate,
  isSubscriptionInGracePeriod,
  subscriptionTier,
}) => {
  if (editorSeatCount > 1) {
    if (isSubscriptionInGracePeriod) {
      return (
        <p className="expirationContainer">
          {
            "Your subscription will be automatically switched to Basic and all team members will lose access to the team."
          }
        </p>
      );
    }

    return (
      <p className="expirationContainer">
        {`Your subscription will be automatically switched to Basic and all team members will lose access to the team on `}
        <span className={style.expirationDate}>{subscriptionExpirationDate}.</span>
      </p>
    );
  }

  return (
    <p className="expirationContainer">
      {`Your subscription will be automatically switched to Basic and you will lose access to all ${getTierDisplayName(subscriptionTier)} features on `}
      <span className={style.expirationDate}>{subscriptionExpirationDate}.</span>
    </p>
  );
};

export const SubscriptionCancelModal: React.FC<{
  open: boolean;
  isLoading: boolean;
  editorSeatCount: number;
  isSubscriptionInGracePeriod: boolean;
  subscriptionExpirationDate: string;
  subscriptionTier: SubscriptionTier;
  onClose: () => void;
  onCancelSubscription: () => void;
}> = ({
  open,
  isLoading,
  editorSeatCount,
  isSubscriptionInGracePeriod,
  subscriptionExpirationDate,
  subscriptionTier,
  onClose,
  onCancelSubscription,
}) => {
  const tracker = useTracker();

  return (
    <Modal open={open} title="Cancel subscription" size="small" onClose={onClose}>
      <div className={style.modalContainer}>
        <div>
          <CancelMessage
            editorSeatCount={editorSeatCount}
            isSubscriptionInGracePeriod={isSubscriptionInGracePeriod}
            subscriptionExpirationDate={subscriptionExpirationDate}
            subscriptionTier={subscriptionTier}
          />
          <p>Are you sure you want to cancel?</p>
        </div>

        <div className={style.modalButtonContainer}>
          <Button
            onClick={() => {
              tracker.trackButtonClick({
                action: ElementClickAction.CANCEL,
                location: ElementClickLocation.CANCEL_SUBSCRIPTION_MODAL,
              });
              onClose();
            }}
            disabled={isLoading}
            variant="grayscale"
          >
            Not now
          </Button>
          <Button
            onClick={() => {
              onCancelSubscription();
              tracker.trackButtonClick({
                action: ElementClickAction.CONFIRM,
                location: ElementClickLocation.CANCEL_SUBSCRIPTION_MODAL,
              });
            }}
            disabled={isLoading}
            variant="red"
          >
            Cancel subscription
          </Button>
        </div>
      </div>
    </Modal>
  );
};
